// What the slide renderer needs from the bid's PowerPoint template (plans/SLIDES_ARTEFACT.md
// "Rendering", AB#177, AB#179): the template's colours and fonts, and the MASTER it draws under every
// slide (background, pictures and decorative shapes, footer, slide number), all from
// `GET /v1/bids/:bid/slides-template` (backend/domain/slide-master.ts, read structurally: the web
// cannot import backend source). A template-less bid gets a neutral default and no master.
import type { Box } from './model'
import { SLIDE_W } from './model'

/** One path of an icon: a bare string is a filled path; the library's paths say how they paint. */
export type IconPath = string | { d: string; fill: boolean; stroke: boolean; strokeWidth?: number }

export interface IconDef {
  id: string
  viewBox: string
  paths: IconPath[]
}

// ── the template's drawing, as the slides-template route serves it (EMU, image URLs) ────────────

/** A resolved colour: RRGGBB (no '#'), its opacity, the theme slot it is when unmodified. */
export interface DrawingColor {
  hex: string
  alpha?: number
  slot?: string
}

/** One permissive object, as the route's schema: `type` says which fields are set. */
export interface DrawingFill {
  type: 'none' | 'solid' | 'gradient' | 'picture'
  color?: DrawingColor
  /** gradient: `linear` (angle in degrees, clockwise from left→right), else a path gradient from the centre */
  shape?: 'linear' | 'circle' | 'rect' | 'shape'
  angle?: number
  stops?: Array<{ pos: number; color: DrawingColor }>
  /** picture: a URL (`/v1/blobs/<sha256>`); `tile` = repeated, else stretched */
  image?: string
  tile?: boolean
  alpha?: number
}

export interface DrawingLine {
  color: DrawingColor
  /** EMU */
  width: number
  dash: string | null
}

export interface DrawingRun {
  text: string
  field?: 'slidenum' | 'datetime'
  /** points */
  size?: number
  bold?: boolean
  italic?: boolean
  underline?: boolean
  color?: DrawingColor
  font?: string
}

export interface DrawingText {
  anchor: 't' | 'ctr' | 'b'
  /** EMU */
  insets: { l: number; t: number; r: number; b: number }
  paragraphs: Array<{ align: 'l' | 'ctr' | 'r' | 'just'; level: number; runs: DrawingRun[] }>
}

export interface EmuBox {
  x: number
  y: number
  cx: number
  cy: number
}

/** A shape, picture or group of the master or a layout; `kind` says which fields are set. */
export interface DrawingNode {
  kind: 'shape' | 'picture' | 'group'
  name?: string
  box: EmuBox
  rot: number
  flipH: boolean
  flipV: boolean
  /** shape */
  geometry?: { type: 'preset' | 'custom'; preset?: string; adj?: Record<string, number>; paths?: Array<{ d: string; fill: boolean; stroke: boolean }> }
  fill?: DrawingFill
  line?: DrawingLine | null
  text?: DrawingText | null
  /** picture: URLs */
  image?: string
  svg?: string | null
  crop?: { l: number; t: number; r: number; b: number } | null
  /** group: members in slide coordinates of the group's unrotated frame */
  children?: unknown[]
}

export type ChromeType = 'dt' | 'ftr' | 'sldNum' | 'hdr'

export interface ChromeDrawing {
  type: ChromeType
  visible: boolean
  box: EmuBox | null
  text: DrawingText | null
}

/** One placeholder of a template layout, as the slides-template route serves it (EMU). */
export interface TemplatePlaceholder {
  type: string
  idx?: number | null
  box: EmuBox | null
  /** First-level font size in points. */
  fontSize: number | null
  /** Its text insets in EMU, when the template sets any (AB#311). */
  insets?: { l: number; t: number; r: number; b: number }
  /** Its first-level text style, in the slide model's forms; what the template does not set is absent (AB#463). */
  color?: string
  fontFamily?: string
  fontWeight?: 700
  fontStyle?: 'italic'
  align?: 'left' | 'center' | 'right' | 'justify'
  valign?: 'top' | 'middle' | 'bottom'
}

/** One layout of the template, as the slides-template route serves it. */
export interface TemplateLayout {
  /** Its identity (its part, e.g. `slideLayout7`): what a slide stores as `layout`. */
  id: string
  path: string
  /** Localised, display only. */
  name: string
  type: string
  placeholders: TemplatePlaceholder[]
  /** Its own background; null = the master's. */
  background?: DrawingFill | null
  /** false = the master's shapes are hidden on this layout (the background stays). */
  showMasterShapes?: boolean
  shapes?: DrawingNode[]
  chrome?: ChromeDrawing[]
}

/** The slice of `GET /v1/bids/:bid/slides-template` the editor reads. */
export interface SlidesTemplateLike {
  slideSize: { cx: number; cy: number }
  colors: Partial<Record<string, string>>
  fonts: { major: string; minor: string }
  layouts: TemplateLayout[]
  /** The master's background and decorative shapes (AB#173). */
  background?: DrawingFill
  shapes?: DrawingNode[]
}

/** What the renderer draws under a slide's elements: the template's master and its layouts. */
export interface MasterDrawing {
  /** The template's slide width in EMU (its drawing is mapped onto the 1280-px canvas). */
  slideCx: number
  background: DrawingFill
  shapes: DrawingNode[]
  layouts: TemplateLayout[]
}

export interface RenderTheme {
  /** Theme slot → RRGGBB (no '#'), as the template parser stores it. */
  colors: Record<string, string>
  fonts: { major: string; minor: string }
  /** null = no template: the slide is painted `lt1`, with no chrome. */
  master: MasterDrawing | null
}

/** The theme colour slots an element may reference, in the order the swatches show them. */
export const THEME_SLOTS = ['dk1', 'lt1', 'dk2', 'lt2', 'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6'] as const
export type ThemeSlot = (typeof THEME_SLOTS)[number]

/** A template-less bid: Office's default palette, on fonts every browser and rasteriser has. */
export const DEFAULT_THEME: RenderTheme = {
  colors: {
    dk1: '000000', lt1: 'FFFFFF', dk2: '44546A', lt2: 'E7E6E6',
    accent1: '4472C4', accent2: 'ED7D31', accent3: 'A5A5A5', accent4: 'FFC000', accent5: '5B9BD5', accent6: '70AD47',
  },
  fonts: { major: 'Georgia', minor: 'Arial' },
  master: null,
}

// ── fonts ────────────────────────────────────────────────────────────────────────────────────────

/** Families that read as serif: their fallback stays serif, so a missing font keeps its look. */
const SERIF = /georgia|times|cambria|garamond|palatino|book antiqua|baskerville|serif|didot|bodoni/i
/** What stands in for a template font the machine lacks (in the browser and in render_slide's page). */
export const SANS_FALLBACK = ['Arial', 'Helvetica', 'sans-serif'] as const
export const SERIF_FALLBACK = ['Georgia', "'Times New Roman'", 'serif'] as const

/**
 * A CSS font stack for a template font: its own name first, then a fallback list of the same
 * family kind (PowerPoint has no fallback list, so the template names one font; the browser needs
 * one in case that font is not installed).
 */
export function fontStack(name: string): string {
  const fallback = SERIF.test(name) && !/sans/i.test(name) ? SERIF_FALLBACK : SANS_FALLBACK
  const own = `'${name.replace(/['\\]/g, '')}'`
  return [own, ...fallback.filter((f) => f.replace(/'/g, '').toLowerCase() !== name.toLowerCase())].join(', ')
}

// ── template → render theme ──────────────────────────────────────────────────────────────────────

/** EMU box → logical px on the 1280-wide canvas. */
export function emuToPx(box: { x: number; y: number; cx: number; cy: number }, slideCx: number): Box {
  const k = SLIDE_W / slideCx
  return { x: box.x * k, y: box.y * k, w: box.cx * k, h: box.cy * k }
}

/** The render theme of a template (its colours over the default ones, its fonts, its master), or the default. */
export function themeFromTemplate(tpl: SlidesTemplateLike | null): RenderTheme {
  if (!tpl) return DEFAULT_THEME
  const colors: Record<string, string> = { ...DEFAULT_THEME.colors }
  for (const [k, v] of Object.entries(tpl.colors)) if (v && /^[0-9a-fA-F]{6}$/.test(v)) colors[k] = v.toUpperCase()
  const master: MasterDrawing | null =
    tpl.slideSize.cx > 0
      ? { slideCx: tpl.slideSize.cx, background: tpl.background ?? { type: 'none' }, shapes: tpl.shapes ?? [], layouts: tpl.layouts }
      : null
  return {
    colors,
    fonts: { major: tpl.fonts.major || DEFAULT_THEME.fonts.major, minor: tpl.fonts.minor || DEFAULT_THEME.fonts.minor },
    master,
  }
}

/**
 * The template layout a slide is drawn on: the one its `layout` id names (a layout's identity —
 * types are all `cust` and names repeat in a corporate template), else, for a slide created before
 * layout ids, the one of its `layout` type, else the blank layout, else the first (the master
 * stays visible). As the backend's `findLayout` (domain/slide-theme.ts).
 */
export function layoutFor(master: MasterDrawing, layout: string | undefined): TemplateLayout | null {
  const own = layout ? (master.layouts.find((l) => l.id === layout) ?? master.layouts.find((l) => l.type === layout)) : undefined
  return own ?? master.layouts.find((l) => l.type === 'blank') ?? master.layouts[0] ?? null
}
